/**
 * Batch 18-F11.20 — Human-readable label helpers for the
 * `CompanionEvidenceTrustCeiling` union.
 *
 * Lives in a separate file from
 * `MobileTrainingFallbackLaunchInterstitial.tsx` so that the
 * interstitial source itself stays free of the lowercase literal
 * `'verified'` — a load-bearing invariant locked by the F11.5
 * source-pin (`/\bverified\b/` is asserted absent in the
 * interstitial source so that fallback evidence cannot be
 * accidentally re-labelled as verified). This helper is the only
 * place that maps the lowercase enum value to its user-visible
 * label.
 *
 * Pure functions, no DOM, no globals. Reused by F11.20 callers
 * that want to surface a `CompanionRuntimeCapabilityResult` as
 * explanatory copy in the interstitial.
 */

import type { CompanionEvidenceTrustCeiling } from './companionPlatformCapabilities';

export function getTrustCeilingLabel(
  ceiling: CompanionEvidenceTrustCeiling,
): string {
  switch (ceiling) {
    case 'weak_launch_only':
      return 'Launch-only / weak';
    case 'self_attested':
      return 'Self-attested';
    case 'likely':
      return 'Likely engagement';
    // OD-35: the top ceiling is a manager's or provider's attestation (companionPlatformCapabilities), with no named
    // verifier at this level — so it is a confirmation, never a bare "Verified".
    case 'verified':
      return 'Confirmed by Manager or Provider';
  }
}

export function isVerifiedCeiling(
  ceiling: CompanionEvidenceTrustCeiling,
): boolean {
  return ceiling === 'verified';
}
